import { forwardRef, memo, useCallback, type ForwardRefExoticComponent, type RefAttributes } from 'react';
import { cva, VariantProps } from 'class-variance-authority';
import type { CKEditor } from '@ckeditor/ckeditor5-react';
import type { InlineEditor as CKInlineEditor } from 'ckeditor5';
import { AlertTriangleIcon } from '@page/icons';

import { cn } from '../../lib/styles';
import { Label } from '../../designSystem/Label';
import { Tooltip } from '../../designSystem/Tooltip';
import { InlineEditor, type InlineEditorInstance, InlineEditorProps } from '@page/core/components/editor/InlineEditor';

type RichTextInputEditorComponent = ForwardRefExoticComponent<InlineEditorProps & RefAttributes<InlineEditorInstance>>;

const richTextInputVariants = cva('relative flex items-center w-full', {
  variants: {
    variant: {
      default:
        'bg-bg-inter-secondary-default rounded-sm outline-1 outline-bg-inter-secondary-default focus-within:outline-border-input-select transition-all',
      unstyled: 'border-none outline-none',
    },
    size: {
      sm: 'px-2 pt-2 pb-1 min-h-8',
      md: 'p-2 min-h-10',
    },
  },
  defaultVariants: {
    variant: 'default',
    size: 'sm',
  },
});

export type RichTextInputProps = Omit<InlineEditorProps, 'data' | 'onDataChange' | 'onChange'> &
  VariantProps<typeof richTextInputVariants> & {
    id?: string;
    value?: InlineEditorProps['data'];
    onChange?: InlineEditorProps['onDataChange'];
    headingConfig?: InlineEditorProps['headingConfig'];
    defaultBlockType?: InlineEditorProps['defaultBlockType'];
    label?: React.ReactNode;
    error?: React.ReactNode;
    leftIcon?: React.ReactNode;
    rightIcon?: React.ReactNode;
    containerClassName?: string;
    editorClassName?: string;
    editorStyle?: React.CSSProperties;
    leftIconClassName?: string;
    rightIconClassName?: string;
    className?: string;
    placeholder?: string;
    placeholderClassName?: string;
    additionalContent?: React.ReactNode;
    /** Content rendered at the end of the container, aligned to the bottom-right. */
    suffix?: React.ReactNode;
    onSubmit?: (event: React.KeyboardEvent<HTMLDivElement>) => void;
    onKeyDown?: (event: React.KeyboardEvent<HTMLDivElement>) => void;
    /** When true, plain Enter triggers onSubmit and Shift+Enter inserts a new line. */
    submitOnEnter?: boolean;
    EditorComponent?: RichTextInputEditorComponent;
    editorProps?: Partial<InlineEditorProps>;
  };

const RichTextInputBase = (
  {
    id,
    value,
    onChange,
    headingConfig,
    defaultBlockType,
    className,
    label,
    error,
    leftIcon,
    rightIcon,
    containerClassName,
    editorClassName,
    editorStyle,
    leftIconClassName,
    rightIconClassName,
    size = 'sm',
    variant = 'default',
    toolbar,
    readonly,
    autofocus,
    onKeyDown,
    onSubmit,
    onReady,
    onFocus,
    onBlur,
    placeholder,
    placeholderClassName,
    additionalContent,
    suffix,
    submitOnEnter,
    EditorComponent = InlineEditor,
    editorProps,
    ...rest
  }: RichTextInputProps,
  ref: React.ForwardedRef<CKEditor<CKInlineEditor>>,
) => {
  const actualId = id;

  const getContainerStyles = () => {
    return cn(richTextInputVariants({ variant, size }), error && 'outline-text-destructive prose', containerClassName);
  };

  const getEditorStyles = () => {
    return cn(
      'w-full font-body-md text-text-primary placeholder:text-text-tertiary',
      leftIcon && 'pl-6',
      rightIcon && 'pr-6',
      editorClassName,
    );
  };

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (event.key === 'Enter' && (event.ctrlKey || event.metaKey)) {
        onSubmit?.(event);
      }

      onKeyDown?.(event);
    },
    [onKeyDown, onSubmit],
  );

  const handleKeyDownCapture = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (!submitOnEnter) return;
      if (event.key === 'Enter' && !event.shiftKey && !event.ctrlKey && !event.metaKey) {
        event.preventDefault();
        event.stopPropagation();
        onSubmit?.(event);
      }
    },
    [submitOnEnter, onSubmit],
  );

  const renderRightIcon = () => {
    const icon =
      !label && error ? (
        <Tooltip label={error} disabled={!error} withinPortal className="z-1000" open>
          <AlertTriangleIcon className="size-5 fill-icon-error cursor-pointer" />
        </Tooltip>
      ) : (
        rightIcon
      );

    return <div className={cn('absolute flex items-center top-2 right-3 z-10', rightIconClassName)}>{icon}</div>;
  };

  const editorElement = (
    <div className={getContainerStyles()} onKeyDown={handleKeyDown} onKeyDownCapture={handleKeyDownCapture}>
      {leftIcon && <div className={cn('absolute left-3 flex items-center z-10', leftIconClassName)}>{leftIcon}</div>}

      <div className={cn('normalized-markdown font-body-md', getEditorStyles())} style={editorStyle}>
        <EditorComponent
          ref={ref}
          data={value}
          toolbar={toolbar}
          headingConfig={headingConfig}
          defaultBlockType={defaultBlockType}
          readonly={readonly}
          autofocus={autofocus}
          onDataChange={onChange}
          onReady={onReady}
          onFocus={onFocus}
          onBlur={onBlur}
          {...rest}
          {...editorProps}
        />
      </div>

      {renderRightIcon()}

      {placeholder && !value && !defaultBlockType && (
        <div
          data-slot="placeholder"
          className={cn(
            'pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 text-text-tertiary font-body-md',
            placeholderClassName,
          )}
        >
          {placeholder}
        </div>
      )}

      {additionalContent}

      {suffix && <div className="flex items-end self-end shrink-0">{suffix}</div>}
    </div>
  );

  if (!label) return editorElement;

  return (
    <div className={cn('flex flex-col', className)}>
      <Label htmlFor={actualId} className="mb-2">
        {label}
      </Label>

      {editorElement}

      <div className="h-5 pt-1 text-text-destructive font-body-sm text-left">{error}</div>
    </div>
  );
};

export const RichTextInput = memo(forwardRef(RichTextInputBase));
